import React, { useEffect, useRef, useState } from "react";
import styles from "./addProductDetails.module.scss";
import {
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Paper,
  Select,
  MenuItem,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { Col, Row } from "react-bootstrap";
import { DESTINATIONS } from "../../utils/common/constants";
import { useS3Upload } from "../../customHooks/S3Upload";
import PROFILE_PIC from "../../assets/images/profileImg.png";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import {
  createProduct,
  updateProduct,
  getAllProduct,
} from "../../store/actions";
import CropImageUpload from "../../components/customComponents/manageBlog/CropImageUpload";

const DRAFT_PRODUCT_IMAGES_KEY = "draft_productImages";

const productDetailsData = [
  {
    id: 1,
    label: "Product Name*",
    inputkey: "name",
    type: "text",
  },
  {
    id: 2,
    label: "Category*",
    inputkey: "categoryId",
    type: "text",
  },
  {
    id: 3,
    label: "Price ₹*",
    inputkey: "price",
    type: "text",
  },
  {
    id: 4,
    label: "Weight *",
    inputkey: "weight",
    type: "text",
  },
  {
    id: 11,
    label: "SI Unit*",
    inputkey: "siUnit",
    type: "text",
  },
  // {
  //   id: 5,
  //   label: "SKU*",
  //   inputkey: "skuId",
  //   type: "text",
  // },
  {
    id: 6,
    label: "HSN Code *",
    inputkey: "hsnCode",
    type: "text",
  },
  {
    id: 7,
    label: "GST % *",
    inputkey: "gst",
    type: "number",
  },
  {
    id: 8,
    label: "",
    inputkey: "keywords",
    type: "text",
  },
  {
    id: 9,
    label: "Description*",
    inputkey: "description",
    type: "text",
  },
  {
    id: 10,
    label: "Quantity*",
    inputkey: "quantity",
    type: "text",
  },
];

const InitialState = {
  imageUrl: "",
  weight: "",
  quantity: "",
  name: "",
  description: "",
  price: "",
  categoryId: [],
  skuId: "",
  hsnCode: "",
  gst: "",
  keywords: [],
  siUnit: "",
  detailDescription: "",
  status: 1,
  productImages: [],
};

const createBlankProductDetails = () => ({
  ...InitialState,
  categoryId: [...InitialState.categoryId],
  keywords: [...InitialState.keywords],
  productImages: [...InitialState.productImages],
});

const templateFilterOptions = createFilterOptions({
  stringify: (option = {}) =>
    [
      option?.skuId || option?.sku || "",
      option?.name || option?.title || "",
      option?.categoryName || option?.category?.name || "",
    ]
      .filter(Boolean)
      .join(" "),
});

const getTemplateLabel = (option = {}) => {
  // if (!option) return "";
  // const sku = option?.skuId || option?.sku || "";
  // const name = option?.name || option?.title || "";
  // if (sku && name) {
  //   return `${sku} – ${name}`;
  // }
  // return sku || name || "";

  if (!option) return "";
  const name = option?.name || option?.title || "";
  if (name) {
    return name;
  }
  // return sku || name || "";
};

const AddProductDetails = ({
  product = {},
  categories = [],
  products = [],
}) => {
  const [productDetails, setProductDetails] = useState(() =>
    createBlankProductDetails()
  );
  const navigate = useNavigate();
  const [newKeyword, setNewKeyword] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [additionalImageStates, setAdditionalImageStates] = useState([]); // [{ previewUrl, uploading }]
  const dragIndexRef = useRef(null);
  // single weight input only; no multi-weight UI
  const [, uploadImages] = useS3Upload();
  const dispatch = useDispatch();
  const isEdit = Boolean(product?.id);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [skuLocked, setSkuLocked] = useState(false);
  const productOptionsLength = products?.length || 0;
  const templateOptions = Array.isArray(products)
    ? products.filter(Boolean)
    : [];
  const showTemplateDropdown = !isEdit;

  useEffect(() => {
    // Initialize form when editing based on presence of product id
    if (isEdit) {
      console.log("product", product);
      setProductDetails({
        imageUrl: product?.imageUrl || "",
        weight: Number(product?.weight) ?? "",
        quantity: product?.quantity || "",
        name: product?.name || "",
        description: product?.description || "",
        price: product?.price || "",
        categoryId: product?.categoryId || [],
        skuId: product?.skuId || "",
        hsnCode: product?.hsnCode || "",
        gst: Number(product?.gst) ?? "",
        keywords: product?.keywords || [],
        detailDescription: product?.detailDescription || "",
        siUnit: product?.siUnit || "",
        status:
          typeof product?.status === "number"
            ? product.status
            : product.status === null
            ? 0
            : 1,
        productImages: product?.productImages || [],
      });
      setImagePreviewUrl(product?.imageUrl || "");
      setAdditionalImageStates(
        (product?.productImages || []).map((url) => ({ previewUrl: url, uploading: false }))
      );
      setSelectedTemplate(null);
      setSkuLocked(false);
    }
  }, [isEdit, product]);

  useEffect(() => {
    if (!isEdit) {
      setProductDetails(createBlankProductDetails());
      setImagePreviewUrl("");
      setAdditionalImageStates([]);
      // Restore any previously uploaded additional image URLs from localStorage
      try {
        const stored = localStorage.getItem(DRAFT_PRODUCT_IMAGES_KEY);
        if (stored) {
          const draft = JSON.parse(stored).filter(Boolean);
          if (draft.length > 0) {
            setProductDetails((prev) => ({ ...prev, productImages: draft }));
            setAdditionalImageStates(draft.map((url) => ({ previewUrl: url, uploading: false })));
          }
        }
      } catch {}
    }
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit && productOptionsLength === 0) {
      dispatch(getAllProduct());
    }
  }, [dispatch, isEdit, productOptionsLength]);

  const handleClearTemplate = () => {
    setProductDetails(createBlankProductDetails());
    setImagePreviewUrl("");
    setAdditionalImageStates([]);
    setSelectedTemplate(null);
    setSkuLocked(false);
    setNewKeyword("");
    localStorage.removeItem(DRAFT_PRODUCT_IMAGES_KEY);
  };

  const handleCroppedImage = async ({ dataUrl, file }) => {
    setImagePreviewUrl(dataUrl || "");
    if (!file) return;
    setImageUploading(true);
    try {
      const uploadUrl = await uploadImages(
        "productImage",
        file,
        DESTINATIONS?.USER_DETAILS,
        false
      );
      if (uploadUrl) {
        setProductDetails((prev) => ({
          ...prev,
          imageUrl: uploadUrl,
        }));
      } else {
        setImagePreviewUrl("");
        setProductDetails((prev) => ({ ...prev, imageUrl: "" }));
      }
    } finally {
      setImageUploading(false);
    }
  };

  const handleClearImage = () => {
    setImagePreviewUrl("");
    setProductDetails((prev) => ({
      ...prev,
      imageUrl: "",
    }));
  };

  const handleAdditionalImageCropped = async ({ dataUrl, file }) => {
    if (!file) return;
    const newIndex = additionalImageStates.length;
    // Show local preview immediately while upload is in progress
    setAdditionalImageStates((prev) => [
      ...prev,
      { previewUrl: dataUrl, uploading: true },
    ]);
    setProductDetails((prev) => ({
      ...prev,
      productImages: [...prev.productImages, ""],
    }));
    try {
      const uploadUrl = await uploadImages(
        "productImage",
        file,
        DESTINATIONS?.USER_DETAILS,
        false
      );
      if (uploadUrl) {
        setProductDetails((prev) => {
          const updated = [...prev.productImages];
          updated[newIndex] = uploadUrl;
          if (!isEdit) {
            try {
              localStorage.setItem(DRAFT_PRODUCT_IMAGES_KEY, JSON.stringify(updated.filter(Boolean)));
            } catch {}
          }
          return { ...prev, productImages: updated };
        });
        // Replace the local dataUrl preview with the real S3 URL
        setAdditionalImageStates((prev) => {
          const updated = [...prev];
          if (updated[newIndex]) {
            updated[newIndex] = { previewUrl: uploadUrl, uploading: false };
          }
          return updated;
        });
      } else {
        setAdditionalImageStates((prev) => prev.filter((_, i) => i !== newIndex));
        setProductDetails((prev) => {
          const updated = prev.productImages.filter((_, i) => i !== newIndex);
          if (!isEdit) {
            try {
              localStorage.setItem(DRAFT_PRODUCT_IMAGES_KEY, JSON.stringify(updated.filter(Boolean)));
            } catch {}
          }
          return { ...prev, productImages: updated };
        });
      }
    } catch {
      setAdditionalImageStates((prev) => prev.filter((_, i) => i !== newIndex));
      setProductDetails((prev) => {
        const updated = prev.productImages.filter((_, i) => i !== newIndex);
        if (!isEdit) {
          try {
            localStorage.setItem(DRAFT_PRODUCT_IMAGES_KEY, JSON.stringify(updated.filter(Boolean)));
          } catch {}
        }
        return { ...prev, productImages: updated };
      });
    }
  };

  const handleRemoveAdditionalImage = (index) => {
    setAdditionalImageStates((prev) => prev.filter((_, i) => i !== index));
    setProductDetails((prev) => {
      const updated = prev.productImages.filter((_, i) => i !== index);
      if (!isEdit) {
        try {
          localStorage.setItem(DRAFT_PRODUCT_IMAGES_KEY, JSON.stringify(updated.filter(Boolean)));
        } catch {}
      }
      return { ...prev, productImages: updated };
    });
  };

  const handleAdditionalDragStart = (e, index) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleAdditionalDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleAdditionalDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIdx = dragIndexRef.current;
    if (dragIdx === null || dragIdx === undefined || dragIdx === dropIndex) return;
    const reorder = (arr) => {
      const next = [...arr];
      const [removed] = next.splice(dragIdx, 1);
      next.splice(dropIndex, 0, removed);
      return next;
    };
    setAdditionalImageStates((prev) => reorder(prev));
    setProductDetails((prev) => {
      const reordered = reorder(prev.productImages);
      if (!isEdit) {
        try {
          localStorage.setItem(DRAFT_PRODUCT_IMAGES_KEY, JSON.stringify(reordered.filter(Boolean)));
        } catch {}
      }
      return { ...prev, productImages: reordered };
    });
    dragIndexRef.current = null;
  };

  // helper: allow only digits and a single decimal point
  const sanitizeDecimal = (value) => {
    if (!value) return "";
    let v = String(value).replace(/[^\d.]/g, "");
    const parts = v.split(".");
    if (parts.length > 1) {
      // keep only first dot
      v = parts[0] + "." + parts.slice(1).join("").replace(/\./g, "");
    }
    return v;
  };

  const handleNumericChange = (key, rawValue) => {
    const val = sanitizeDecimal(rawValue);
    setProductDetails((prev) => ({ ...prev, [key]: val }));
  };

  const mapTemplateToForm = (template = {}) => {
    const blank = createBlankProductDetails();
    const categoryField =
      template?.categoryId ??
      template?.categoryIds ??
      (template?.category ? template.category.id : undefined);
    const safeCategoryId = Array.isArray(categoryField)
      ? [...categoryField]
      : categoryField != null
      ? [categoryField]
      : [];
    const statusRaw =
      typeof template?.status === "number"
        ? template.status
        : Number(template?.status);
    const resolvedStatus = Number.isNaN(statusRaw) ? blank.status : statusRaw;

    return {
      ...blank,
      imageUrl: template?.imageUrl || "",
      weight:
        template?.weight === undefined || template?.weight === null
          ? ""
          : sanitizeDecimal(template.weight),
      quantity:
        template?.quantity === undefined || template?.quantity === null
          ? ""
          : String(template.quantity),
      name: template?.name || "",
      description: template?.description || "",
      price:
        template?.price === undefined || template?.price === null
          ? ""
          : sanitizeDecimal(template.price),
      categoryId: safeCategoryId,
      skuId: template?.skuId || template?.sku || "",
      hsnCode: template?.hsnCode || "",
      gst:
        template?.gst === undefined || template?.gst === null
          ? ""
          : sanitizeDecimal(template.gst),
      keywords: Array.isArray(template?.keywords)
        ? template.keywords.filter(Boolean)
        : [],
      siUnit: template?.siUnit || "",
      detailDescription: template?.detailDescription || "",
      status: resolvedStatus,
      productImages: Array.isArray(template?.productImages)
        ? [...template.productImages]
        : [],
    };
  };

  const handleTemplateSelect = (_, template) => {
    if (!template) {
      handleClearTemplate();
      return;
    }
    const prefilled = mapTemplateToForm(template);
    setProductDetails(prefilled);
    setImagePreviewUrl(prefilled?.imageUrl || "");
    setAdditionalImageStates(
      (prefilled.productImages || []).map((url) => ({ previewUrl: url, uploading: false }))
    );
    setSelectedTemplate(template);
    setSkuLocked(Boolean(prefilled?.skuId));
    setNewKeyword("");
  };

  const handleSubmit = () => {
    if (imageUploading) return;
    const payload = {
      imageUrl: productDetails?.imageUrl,
      weight: Number(productDetails?.weight || 0),
      quantity: Number(productDetails?.quantity),
      name: productDetails?.name,
      description: productDetails?.description,
      price: Number(productDetails?.price),
      categoryId: productDetails?.categoryId,
      ...(productDetails?.skuId && { skuId: productDetails.skuId }), // only include if exists
      hsnCode: productDetails?.hsnCode || "",
      gst: productDetails?.gst === "" ? undefined : Number(productDetails?.gst),
      keywords: productDetails?.keywords,
      siUnit: productDetails?.siUnit,
      detailDescription: productDetails?.detailDescription,
      status: Number(productDetails?.status) === 1 ? 1 : 0,
      productImages: productDetails?.productImages || [],
    };

    console.log("hsn", payload);

    if (isEdit) {
      dispatch(
        updateProduct({
          payload: { ...product, ...payload },
          callback: {
            success: () => {
              dispatch(getAllProduct());
              navigate(-1);
            },
          },
        })
      );
    } else {
      dispatch(
        createProduct({
          payload,
          callback: {
            success: () => {
              localStorage.removeItem(DRAFT_PRODUCT_IMAGES_KEY);
              dispatch(getAllProduct());
              navigate(-1);
            },
          },
        })
      );
    }
  };

  // removed multi-weight confirmation flow

  const handleAddKeyword = () => {
    const trimmed = newKeyword.trim();
    if (trimmed && !productDetails.keywords.includes(trimmed)) {
      setProductDetails({
        ...productDetails,
        keywords: [...productDetails.keywords, trimmed],
      });
      setNewKeyword("");
    }
  };

  // removed add weight chips handler
  console.log("productDetails", productDetails);

  const isFormModified = () => {
    const hasChanges =
      productDetails?.imageUrl !== product?.imageUrl ||
      String(productDetails?.weight ?? "") !== String(product?.weight ?? "") ||
      productDetails?.quantity !== product?.quantity ||
      productDetails?.name !== product?.name ||
      productDetails?.price !== product?.price ||
      JSON.stringify(productDetails?.categoryId) !==
        JSON.stringify(product?.categoryId) ||
      productDetails?.hsnCode !== (product?.hsnCode || "") ||
      String(productDetails?.gst ?? "") !== String(product?.gst ?? "") ||
      JSON.stringify(productDetails?.keywords) !==
        JSON.stringify(product?.keywords) ||
      productDetails?.siUnit !== (product?.siUnit || "") ||
      productDetails?.detailDescription !== product?.detailDescription ||
      productDetails?.description !== product?.description ||
      productDetails?.status !== product?.status ||
      productDetails?.gst !== product?.gst ||
      productDetails?.hsnCode !== product?.hsnCode ||
      JSON.stringify(productDetails?.productImages) !==
        JSON.stringify(product?.productImages || []);
    // productDetails?.skuId !== product?.skuId ||

    const isValid =
      productDetails?.name &&
      productDetails?.imageUrl &&
      productDetails?.weight !== "" &&
      productDetails?.quantity &&
      productDetails?.price &&
      productDetails?.categoryId &&
      productDetails?.categoryId?.length > 0 &&
      productDetails?.keywords?.length > 0 &&
      productDetails?.siUnit &&
      productDetails?.detailDescription &&
      productDetails?.description &&
      productDetails?.hsnCode &&
      productDetails?.gst;
    //  && productDetails?.skuId
    return isEdit ? hasChanges && isValid : isValid;
  };

  return (
    <div className={styles.container}>
      <Paper className={styles.wrapper}>
        <Row className={styles.titleContainer}>
          <Col>
            <div className={styles.titleRow}>
              <div className={styles.title}>Product Details</div>
              {showTemplateDropdown && (
                <div className={styles.templateControls}>
                  <Autocomplete
                    className={styles.templateAutocomplete}
                    options={templateOptions}
                    value={selectedTemplate}
                    onChange={handleTemplateSelect}
                    filterOptions={templateFilterOptions}
                    getOptionLabel={getTemplateLabel}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Use existing product as template"
                        placeholder="Search title"
                      />
                    )}
                    noOptionsText="No matching products"
                  />
                  {selectedTemplate && (
                    <Button size="small" onClick={handleClearTemplate}>
                      Clear
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>

        <Row className={styles.profilePicRow}>
          <Col className={styles.profilePicContainer}>
            <img
              src={imagePreviewUrl || productDetails?.imageUrl || PROFILE_PIC}
              alt="profile pic"
              className={styles.profilePic}
            />
          </Col>
          <Col className={styles.profilePicActionContainer}>
            <div className={styles.buttonsContainer}>
              <CropImageUpload
                aspect={1}
                cropSize={{ width: 1000, height: 1000 }}
                onCropped={handleCroppedImage}
                onClear={handleClearImage}
              />
            </div>
            <div className={styles.picUploadInfo}>
              {imageUploading
                ? "Uploading cropped image..."
                : "Upload a PNG or JPEG, crop to square, max size 500KB."}
            </div>
          </Col>
        </Row>

        {/* Additional Images (optional, max 7) */}
        <Row className={styles.additionalImagesRow}>
          <Col>
            <div className={styles.additionalImagesLabel}>
              Additional Images{" "}
              <span className={styles.optionalLabel}>(Optional · max 7)</span>
            </div>
            <div className={styles.additionalImagesGrid}>
              {additionalImageStates.map((imgState, index) => (
                <div
                  key={index}
                  className={styles.additionalImageItem}
                  draggable
                  onDragStart={(e) => handleAdditionalDragStart(e, index)}
                  onDragOver={handleAdditionalDragOver}
                  onDrop={(e) => handleAdditionalDrop(e, index)}
                >
                  <img
                    src={imgState.previewUrl || PROFILE_PIC}
                    alt={`Additional ${index + 1}`}
                    className={styles.additionalImageThumb}
                  />
                  {imgState.uploading && (
                    <div className={styles.additionalImageOverlay}>
                      Uploading…
                    </div>
                  )}
                  <button
                    type="button"
                    className={styles.additionalImageRemove}
                    onClick={() => handleRemoveAdditionalImage(index)}
                    disabled={imgState.uploading}
                  >
                    ✕
                  </button>
                  <div className={styles.dragHandle} title="Drag to reorder">
                    ⠿
                  </div>
                </div>
              ))}
              {additionalImageStates.length < 7 && (
                <div className={styles.additionalImageAdd}>
                  <CropImageUpload
                    aspect={1}
                    cropSize={{ width: 1000, height: 1000 }}
                    onCropped={handleAdditionalImageCropped}
                    onClear={() => {}}
                    compact
                  />
                </div>
              )}
            </div>
            <div className={styles.picUploadInfo} style={{ marginTop: 6 }}>
              PNG or JPEG · 1:1 square crop · max 500 KB each · drag to reorder
            </div>
          </Col>
        </Row>

        <Row className={styles.statusRow}>
          <Col>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Number(productDetails?.status) === 1}
                  onChange={(e) =>
                    setProductDetails({
                      ...productDetails,
                      status: e.target.checked ? 1 : 0,
                    })
                  }
                />
              }
              label={
                Number(productDetails?.status) === 1 ? "Active" : "Inactive"
              }
            />
          </Col>
        </Row>

        <div className={styles.formWrapper}>
          <div className={styles.inputGroup}>
            {[
              productDetailsData.slice(0, 3),
              productDetailsData.slice(3, 6),
              productDetailsData.slice(6),
            ].map((group, groupIndex) => (
              <div className={styles.inputRow} key={groupIndex}>
                {group.map((item) => (
                  <div className={styles.inputCol} key={item.inputkey}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      classes={{ root: styles.inputContainer }}
                    >
                      <InputLabel htmlFor={`input-${item.inputkey}`}>
                        {item.label}
                      </InputLabel>

                      {item.inputkey === "categoryId" ? (
                        <Select
                          multiple
                          label={item.label}
                          value={productDetails?.categoryId || []}
                          onChange={(e) =>
                            setProductDetails({
                              ...productDetails,
                              categoryId: e.target.value,
                            })
                          }
                          renderValue={(selected) =>
                            categories
                              .filter((cat) => selected.includes(cat.id))
                              .map((cat) => cat.name)
                              .join(", ")
                          }
                          inputProps={{
                            name: item.inputkey,
                            id: `input-${item.inputkey}`,
                          }}
                        >
                          {categories?.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : item.inputkey === "weight" ? (
                        <OutlinedInput
                          id={`input-${item.inputkey}`}
                          type="text"
                          label={item.label}
                          classes={{
                            root: styles.input,
                            input: styles.inputField,
                          }}
                          value={productDetails?.weight || ""}
                          onChange={(e) =>
                            handleNumericChange("weight", e.target.value)
                          }
                        />
                      ) : item.inputkey === "keywords" ? (
                        <>
                          <TextField
                            variant="outlined"
                            label="Add Keyword *"
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddKeyword();
                              }
                            }}
                            fullWidth
                          />

                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "8px",
                              padding: "10px 0px",
                            }}
                          >
                            {productDetails.keywords.map((keyword, index) => (
                              <Chip
                                key={index}
                                label={keyword}
                                onDelete={() =>
                                  setProductDetails({
                                    ...productDetails,
                                    keywords: productDetails.keywords.filter(
                                      (k) => k !== keyword
                                    ),
                                  })
                                }
                                classes={{ root: styles.chipCLr }}
                                variant="outlined"
                              />
                            ))}
                          </div>
                          <Button
                            variant="outlined"
                            onClick={handleAddKeyword}
                            classes={{
                              root: styles.chipCLr,
                              text: styles.chipCLr,
                            }}
                            style={{ marginTop: "8px", marginBottom: "8px" }}
                          >
                            Add Keyword
                          </Button>
                        </>
                      ) : item.inputkey === "siUnit" ? (
                        // <OutlinedInput
                        //   id={`input-${item.inputkey}`}
                        //   type="text"
                        //   label={item.label}
                        //   classes={{
                        //     root: styles.input,
                        //     input: styles.inputField,
                        //   }}
                        //   value={productDetails?.siUnit || ""}
                        //   onChange={(e) =>
                        //     setProductDetails({
                        //       ...productDetails,
                        //       siUnit: e.target.value,
                        //     })
                        //   }
                        // />
                        <Select
                          label={item.label}
                          value={productDetails?.siUnit || []}
                          onChange={(e) =>
                            setProductDetails({
                              ...productDetails,
                              siUnit: e.target.value,
                            })
                          }
                          // renderValue={(selected) =>
                          //   categories
                          //     .filter((cat) => selected.includes(cat.id))
                          //     .map((cat) => cat.name)
                          //     .join(", ")
                          // }
                          inputProps={{
                            name: item.inputkey,
                            id: `input-${item.inputkey}`,
                          }}
                        >
                          {[{id:1,name:'g'},{id:2,name:'kg'}]?.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <OutlinedInput
                          id={`input-${item.inputkey}`}
                          type={item.type}
                          label={item.label}
                          classes={{
                            root: styles.input,
                            input: styles.inputField,
                          }}
                          disabled={
                            item.inputkey === "skuId" && Boolean(skuLocked)
                          }
                          value={productDetails?.[item.inputkey] || ""}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (item.inputkey === "gst") {
                              val = sanitizeDecimal(val);
                            }
                            setProductDetails({
                              ...productDetails,
                              [item.inputkey]: val,
                            });
                          }}
                        />
                      )}
                    </FormControl>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Paper>

      <Paper className={styles.wrapper}>
        <Row className={styles.titleContainer}>
          <Col className={styles.title}>Detailed Description*</Col>
          <OutlinedInput
            id="detailed-description"
            label=""
            multiline
            minRows={5}
            value={productDetails?.detailDescription || ""}
            onChange={(e) =>
              setProductDetails({
                ...productDetails,
                detailDescription: e.target.value,
              })
            }
            maxRows={30}
            fullWidth
            className={styles.textarea}
          />
        </Row>
        <div className={styles.formWrapper}>
          <Row className={styles.formButtonRow}>
            <Col className={styles.formBtnContainer}>
              <Button
                variant="outlined"
                classes={{ outlined: styles.cancelButton }}
                onClick={() => navigate(-1)}
                color="error"
              >
                Cancel
              </Button>
            </Col>
            <Col className={styles.formBtnContainer}>
              {/* <CustomButton
                title={" Save"}
                txtClr={"#FFFFFF"}
                background={
                  !isFormModified() ? "grey" : "hsla(187, 98%, 16%, 1)"
                }
                width={"90px"}
                height={"34px"}
                borderRadius={"12px"}
                disabled={!isFormModified()}
              /> */}
              <Button
                variant="contained"
                color="success"
                classes={{
                  contained: styles?.saveBtn,
                  disabled: styles.disabledSaveBtn,
                }}
                disabled={
                  !isFormModified() ||
                  imageUploading ||
                  additionalImageStates.some((s) => s.uploading)
                }
                onClick={handleSubmit}
              >
                Save
              </Button>
            </Col>
          </Row>
        </div>
      </Paper>
      {/* Removed multi-weight confirmation modal */}
    </div>
  );
};

const mapStateToProps = (state) => ({
  product: state?.product?.product,
  products: state?.product?.products,
  categories: state?.category?.categories,
});

export default connect(mapStateToProps)(AddProductDetails);
